import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";
import { EntityStates } from "../components/Actors/Entity";
import { ScrollStates } from "../components/Assets/Scroll";
import { Vector3 } from "three";
import { newPosition } from "../../../utils/newPosition";
import { Entity } from "../types/Entity";
import { generateQuizQuestions } from "../../../utils/generateQuizQuestions";
import { QuestionTypes } from "../constants/QuestionTypes";
import { GameStates } from "../constants/GameStates";
import { GameModes } from "../constants/GameModes";
import { QuizQuestion } from "../types/QuizQuestion";

export const useGameStore = create(
  persist(
    subscribeWithSelector((set: any, get: any) => ({
      gameSetId: null,
      level: null,
      mode: GameModes.LEARN,
      isSoundOn: true,
      setIsSoundOn: (isSoundOn: boolean) => set({ isSoundOn }),
      isMusicOn: true,
      setIsMusicOn: (isMusicOn: boolean) => set({ isMusicOn }),
      isFullscreen: false,
      setIsFullscreen: (isFullscreen: boolean) => set({ isFullscreen }),
      currentStage: 0,
      currentItem: null,
      storedTreeData: [],
      gameState: GameStates.MENU,
      setGameState: (gameState: string) => set({ gameState }),

      // PLAYER
      playerStrength: 10,
      memoryLevel: 100,
      setMemoryLevel: (memoryLevel: number) => set({ memoryLevel }),
      staminaLevel: 100,
      setStaminaLevel: (staminaLevel: number) => set({ staminaLevel }),
      playerPosition: [],

      // ENTITY AI
      entityStrength: null,
      entityFinalStrength: null,
      entityPosition: [],
      entity: {
        id: "entity",
        originPosition: new Vector3(
          newPosition(20, 70),
          0,
          newPosition(20, 70)
        ),
        state: EntityStates.IDLE,
        corruptionLevel: 100,
        canSeeTarget: false,
      },
      setEntity: (updates: Partial<Entity>) =>
        set((state: any) => ({
          entity: { ...state.entity, ...updates },
        })),

      // SCROLL
      scrollPosition: [],
      scrollState: ScrollStates.REMOVED,
      setScrollState: (scrollState: string) => set({ scrollState }),
      scrollValue: 0,
      setScrollValue: (scrollValue: number) => set({ scrollValue }),

      // POUCH
      pouchPosition: [],
      pouchState: ScrollStates.REMOVED,
      setPouchState: (pouchState: string) => set({ pouchState }),
      pouchValue: 0,
      setPouchValue: (pouchValue: number) => set({ pouchValue }),

      // POINTS
      goodAnswers: 0,
      goodAnswerStreak: 0,
      answerQuantity: 0,
      coins: 0,
      setCoins: (coins: number) => set({ coins }),

      loadNewGame: (gameSetId: string) => {
        set({
          gameSetId,
          level: null,
          mode: GameModes.LEARN,
          isSoundOn: true,
          isMusicOn: true,
          isFullscreen: false,
          currentStage: 0,
          currentItem: null,
          gameState: GameStates.MENU,
          playerStrength: 10,
          memoryLevel: 100,
          staminaLevel: 100,
          playerPosition: [],
          entityStrength: null,
          entityFinalStrength: null,
          entityPosition: [],
          entity: {
            id: "entity",
            originPosition: new Vector3(
              newPosition(20, 70),
              0,
              newPosition(20, 70)
            ),
            state: EntityStates.IDLE,
            corruptionLevel: 100,
            canSeeTarget: false,
          },
          scrollPosition: [],
          scrollState: ScrollStates.REMOVED,
          scrollValue: 0,
          pouchPosition: [],
          pouchState: ScrollStates.REMOVED,
          pouchValue: 0,
          goodAnswers: 0,
          goodAnswerStreak: 0,
          answerQuantity: 0,
          coins: 0,
        });
      },

      startGame: async (
        mode: string,
        isSoundOn: boolean,
        isMusicOn: boolean,
        isFullscreen: boolean
      ) => {
        const gameSetId = get().gameSetId;
        const level = await generateQuizQuestions(gameSetId);
        const currentItem = level[0];
        const entityStrength = 25 / level.length;
        const entityFinalStrength = 100 / level.length;
        await set({
          level,
          mode,
          isSoundOn,
          isMusicOn,
          isFullscreen,
          memoryLevel: 100,
          staminaLevel: 100,
          entityStrength,
          entityFinalStrength,
          gameState: GameStates.GAME,
          currentStage: 0,
          currentItem,
          answerQuantity: level.length,
        });
      },

      nextStage: () => {
        set((state: any) => {
          if (
            state.currentStage === state.level.length - 1 &&
            state.memoryLevel > 0
          ) {
            return {
              currentStage: 0,
              currentItem: null,
              level: null,
              gameState: GameStates.WON,
            };
          }
          if (
            state.currentStage === state.level.length - 1 &&
            state.memoryLevel === 0
          ) {
            return {
              currentStage: 0,
              currentItem: null,
              level: null,
              gameState: GameStates.GAME_OVER,
            };
          }

          const currentStage = state.currentStage + 1;
          const currentItem = state.level[currentStage];
          return { currentStage, currentItem };
        });
      },

      getQuestion: () => {
        set({ gameState: GameStates.QUIZ });
      },

      playerAnswer: (playerAnswer: string) => {
        const currentItem = get().currentItem;
        const currentGameMode = get().mode;
        const currentLevel = get().level;
        if (!currentItem) return;

        const isCorrect =
          currentItem.type === QuestionTypes.MULTIPLE_CHOICE
            ? playerAnswer === currentItem.solution
            : playerAnswer.trim().toLowerCase() ===
              currentItem.solution.trim().toLowerCase();

        if (isCorrect) {
          set((state: any) => ({
            goodAnswers: state.goodAnswers + 1,
            gameState: GameStates.GAME,
            entity: {
              ...state.entity,
              corruptionLevel:
                state.entity.corruptionLevel - state.playerStrength,
            },
            goodAnswerStreak: state.goodAnswerStreak + 1,
          }));
        } else {
          if (currentGameMode === GameModes.LEARN) {
            const newItem: QuizQuestion = {
              question: currentItem.question,
              solution: currentItem.solution,
              type: QuestionTypes.SHORT_ANSWER,
            };
            const updatedLevel = [...currentLevel, newItem];
            set((state: any) => ({ level: updatedLevel }));
          }
          set((state: any) => ({
            memoryLevel: state.memoryLevel - state.entityFinalStrength,
            entity: {
              ...state.entity,
              corruptionLevel:
                state.entity.corruptionLevel + 5 * state.playerStrength,
            },
            gameState:
              state.mode === GameModes.LEARN
                ? GameStates.SHOW_ANSWER
                : GameStates.GAME,
            goodAnswerStreak: 0,
          }));
        }
        get().nextStage();
      },
    })),
    {
      name: "game-store", // name in localStorage
      partialize: (state) => ({
        gameSetId: state.gameSetId, // only persist gameSetId
      }),
    }
  )
);
